import {
    BaseSyntheticEvent,
    ChangeEvent,
    Children,
    cloneElement,
    isValidElement,
    KeyboardEvent,
    MouseEvent,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { ElementGetter, normalize, useComponentProps } from '@optimus-web/common';
import { MaxSizeChangeProps, Positioner, useClickAway, withScrollAway } from '@optimus-web/helpers';
import { Drawer } from '../Drawer';
import { LabeledDropdownItem } from '../DropdownItem';
import { Input } from '../Input';
import { IOpenableOverlay, OpenableOverlay } from '../OpenableOverlay';
import { defaultSearchValueMapper, DrawerInput, ISelectList, SelectList } from '../Select';
import { Stack } from '../Stack';
import { InputValue, Selected } from '../types';
import { getBreakpointRangeValueFallback } from '../utils/breakpointsFallback';
import { mod } from '../utils/func';
import { InputSpanWrapper } from './style';
import { AutocompleteDropdownItemProps, AutocompleteProps } from './types';

const OVERLAY_MAX_HEIGHT = 500;
const valueMapper = (comp: ReactElement): InputValue => comp.props?.value;

const AutocompleteComponent = ({
    name,
    children,
    searchValueMapper = defaultSearchValueMapper,
    portalContainerId,
    placeholder,
    autoCompleteAttribute,
    virtualized,
    disabled,
    readOnly,
    required,
    size: customSize,
    emptyMessage,
    sizeToAnchor = true,
    value,
    inputType,
    label,
    mobileOptionsHeader,
    helper,
    captionIconHandler,
    captionIconWithTooltip,
    inline,
    autoFocus,
    onSearchChange,
    breakpointRange,
    dataTestId,
    onFocus,
    onBlur,
    onChange,
    inputValuePattern,
    registerScrollAway,
    deregisterScrollAway,
    clearable,
    loading,
}: AutocompleteProps) => {
    const [opened, setOpened] = useState(false);
    const [focused, setFocused] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [keyboardHandled, setKeyboardHandled] = useState(false);
    const [indexChangeReason, setIndexChangeReason] = useState<'user' | 'internal'>('internal');

    const comboInputRef = useRef<HTMLInputElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const drawerRef = useRef<HTMLDivElement | null>(null);
    const drawerInputRef = useRef<HTMLInputElement | null>(null);
    const searchContainerRef = useRef<HTMLDivElement | null>(null);
    const inputWrapperRef = useRef<HTMLSpanElement | null>(null);
    const overlayComponentRef = useRef<IOpenableOverlay | null>(null);
    let listComponentRef: ISelectList | null = null;
    let items: ReactElement[] = [];

    const { registerClickAway, deregisterClickAway } = useClickAway();
    const { size } = useComponentProps('Autocomplete', { size: customSize });

    useEffect(() => {
        if (!opened) {
            setSelectedIndex(0);
            setIndexChangeReason('internal');
        }
        if (opened && overlayComponentRef.current) {
            overlayComponentRef.current.updatePlacement();
        }
    }, [opened]);

    const open = () => {
        if (opened || disabled || readOnly) return;
        setOpened(true);

        if (!isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange))) {
            registerScrollAway({ handler: close, roots: [() => comboInputRef.current, () => overlayRef.current] });
        } else {
            window.setTimeout(() => drawerInputRef.current?.focus(), 500);
        }
    };

    const close = useCallback(() => {
        setOpened(false);
        deregisterScrollAway();
        deregisterClickAway();
        setFocused(false);
        onBlur?.();
    }, [deregisterClickAway, deregisterScrollAway, onBlur]);

    useEffect(() => {
        if (opened) {
            registerClickAway({
                handler: close,
                roots: ((): ElementGetter[] => [
                    () => comboInputRef.current,
                    () => overlayRef.current,
                    () => drawerRef.current,
                ])(),
            });
        }
    }, [close, opened, registerClickAway]);

    const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>, _val: InputValue) => {
        const { value: inputValue } = e.target;

        if (inputValuePattern && !inputValuePattern.test(inputValue)) return;

        setSearchValue(inputValue);
        setSelectedIndex(0);
        setIndexChangeReason('internal');

        onSearchChange?.(inputValue);
        onChange?.(e as ChangeEvent, inputValue);

        if (!opened && inputValue.length > 0) {
            open();
        } else if (opened && !inputValue) {
            close();
        }
    };

    const handleFocus = () => {
        if (!focused) {
            setFocused(true);
            onFocus?.();
        }
    };

    const handleItemClick = (e: BaseSyntheticEvent, itemValue: InputValue, itemLabel: string) => {
        onChange?.(e as ChangeEvent, itemValue);
        setSearchValue(itemLabel);
        onSearchChange?.(itemValue.toString());
        close();
    };

    const handleInputKeyDown = (e: KeyboardEvent<HTMLElement>) => {
        if (!items) return;

        let handled = false;

        if (e.key === 'ArrowDown') {
            if (opened && items.length > 0) {
                setSelectedIndex((prev) => (prev + 1) % items.length);
                setIndexChangeReason('user');
                handled = true;
            }
            if (!opened) {
                open();
                handled = true;
            }
        } else if (e.key === 'ArrowUp') {
            if (opened && items.length > 0) {
                setSelectedIndex((prev) => mod(prev - 1, items.length));
                setIndexChangeReason('user');
                handled = true;
            }
            if (!opened) {
                open();
                handled = true;
            }
        } else if (e.key === 'Enter' && opened && items.length > 0) {
            const item = items[selectedIndex];
            const menuItem = item as React.ReactElement<{
                onClick: (e: MouseEvent | KeyboardEvent, value: InputValue, label: string) => void;
                value: InputValue;
                label: string;
                disabled?: boolean;
            }>;
            menuItem.props.onClick?.(e, menuItem.props.value, menuItem.props.label);
            setSearchValue(menuItem.props.value as string);
            onSearchChange?.(menuItem.props.value as string);
            handled = true;
        } else if (e.key === 'Escape') {
            close();
            handled = true;
        }

        if (handled) {
            e.preventDefault();
            e.stopPropagation();
            setKeyboardHandled(true);
        }
    };

    const normalizedSearchValue = normalize(searchValue);
    const filteredItems = Children.toArray(children).filter(
        (c): c is React.ReactElement<AutocompleteDropdownItemProps> => {
            if (!isValidElement<LabeledDropdownItem>(c)) return false;

            if (!normalizedSearchValue.length || onSearchChange) return true;

            const childSearchValue: Selected | null = searchValueMapper(c) || valueMapper(c)?.toString();
            return childSearchValue ? childSearchValue.includes(normalizedSearchValue) : false;
        }
    );

    items = filteredItems.map((c, index) => {
        const hasFocus = index === selectedIndex;
        return cloneElement(c, {
            focused: hasFocus,
            shouldScrollIntoView: indexChangeReason === 'user' && keyboardHandled,
            keyboardHandled,
            onClick: (e: MouseEvent, currentValue: InputValue) =>
                (c.props.onClick || handleItemClick)(e, currentValue, (c.props.label ?? currentValue).toString()),
            tabIndex: -1,
            role: 'option',
            id: `${name}-${c.props.value}`,
            disabled: c.props.disabled,
            'aria-selected': focused,
        });
    });

    const activeValue = items.length > 0 ? items[selectedIndex]?.props.value : null;

    const inputProps = {
        disabled,
        readOnly,
        required,
        placeholder,
        value: searchValue,
        onFocus: handleFocus,
        ref: inputRef,
        onChange: (e: ChangeEvent<HTMLInputElement> | MouseEvent, val: InputValue) =>
            handleSearchValueChange(e as ChangeEvent<HTMLInputElement>, val),
        onKeyDown: handleInputKeyDown,
        inputSize: size,
        id: name,
        role: 'combobox',
        type: inputType,
        'aria-activedescendant': opened && activeValue ? `${name}-${activeValue}` : undefined,
        'aria-autocomplete': 'list' as const,
        'aria-expanded': opened,
        'aria-haspopup': 'listbox' as const,
        autoComplete: autoCompleteAttribute,
        'data-test-display-value': value,
        'data-test-id': dataTestId,
        label,
        helper,
        captionIconHandler,
        captionIconWithTooltip,
        inline,
        autoFocus,
        loading,
    };

    const listProps = {
        virtualized,
        selectedIndex,
        maxHeight: OVERLAY_MAX_HEIGHT,
        emptyMessage,
        ref: (ref: ISelectList | null) => {
            listComponentRef = ref;
        },
        keyboardHandled,
        searchContainerHeight: searchContainerRef.current?.clientHeight || 0,
    };

    const renderClearableHandler = useMemo(() => {
        if (!clearable || disabled || readOnly) return false;

        return !!searchValue;
    }, [clearable, disabled, readOnly, searchValue]);

    const handleClear = () => {
        handleFocus();
        comboInputRef.current?.focus();

        setSearchValue('');
        onChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>, '');
        onSearchChange?.('');
    };

    return (
        <>
            <InputSpanWrapper ref={inputWrapperRef}>
                <Input
                    {...inputProps}
                    ref={comboInputRef}
                    name={name}
                    size={size}
                    clearable={renderClearableHandler}
                    onClear={handleClear}
                />
            </InputSpanWrapper>

            {isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange)) ? (
                <Drawer open={opened} closeHandler="close" onClose={close} lockScroll ref={drawerRef}>
                    <Stack vertical spacing={mobileOptionsHeader ? '200' : '100'}>
                        {/* @ts-expect-error */}
                        {mobileOptionsHeader || <DrawerInput {...inputProps} size={size} ref={drawerInputRef} />}
                        <SelectList {...listProps}>{items}</SelectList>
                    </Stack>
                </Drawer>
            ) : (
                <OpenableOverlay
                    sizeToAnchor={sizeToAnchor}
                    scrollOnOverflow
                    opened={opened}
                    anchorElement={() => inputWrapperRef.current}
                    innerRef={overlayRef}
                    ref={overlayComponentRef as unknown as React.Ref<Positioner>}
                    maxHeight={OVERLAY_MAX_HEIGHT}
                    portalContainerId={portalContainerId}
                    onMaxSizeChange={({ maxHeight }: MaxSizeChangeProps) =>
                        maxHeight && listComponentRef?.updateMaxHeight(maxHeight)
                    }
                    data-test-overlay-id={name}
                >
                    <SelectList {...listProps}>{items}</SelectList>
                </OpenableOverlay>
            )}
            <input type="hidden" name={name} value={value} />
        </>
    );
};

export default withBreakpointRange(withScrollAway(AutocompleteComponent));
